// PatientDetails.jsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Select from "react-select";
import { FaPencilAlt, FaSpinner } from "react-icons/fa";
import {
  getClinicIdFromToken,
  getValueFromToken,
} from "../../../utils/tokenUtils";
import {
  serviceFetchDoctorList,
  serviceCreateAppointment,
  updatePatientBasicDetails,
} from "../../../services/api";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../../utils/toastUtils";
import { addEditPatientValidation } from "../../../utils/validations/addEditPatientValidation";
const PatientDetails = ({ patient, showAppointmentForm }) => {
  console.log(
    "patient details received by PatientDetails component: ",
    patient
  );
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedPatient, setEditedPatient] = useState({
    firstName: patient.name ? patient.name.split(" ")[0] : "",
    lastName: patient.name ? patient.name.split(" ").slice(1).join(" ") : "",
    age: patient.age,
    gender: patient.gender,
    primary_phone_number: patient.primary_phone_number,
  });

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleEditSubmit = async () => {
    if (editedPatient.age <= 0 || isNaN(editedPatient.age)) {
      showToast("Age must be a positive number", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const currentDate = new Date();
      const approximateDateOfBirth = new Date(
        currentDate.getFullYear() - editedPatient.age,
        currentDate.getMonth(),
        currentDate.getDate()
      )
        .toISOString()
        .slice(0, 10);
        await addEditPatientValidation.validate(
          {
            firstName: editedPatient.firstName ? patient.name.split(" ")[0] : "",
            primary_phone_number: editedPatient.primary_phone_number,
          },
          { abortEarly: false }
        );
      const response = await updatePatientBasicDetails(
        patient.patient_id,
        editedPatient.firstName,
        editedPatient.lastName,
        approximateDateOfBirth,
        editedPatient.gender,
        editedPatient.primary_phone_number
      );

      if (response.status) {
        setIsEditMode(false);
        showToast(response.message, "success");
      } else {
        showToast(response.message, "error");
      }
    } catch (error) {
      console.error("Error updating patient details:", error);
      showToast("Error updating patient details", "error");
    }

    setIsSubmitting(false);
  };

  useEffect(() => {
    console.log(patient, "patient");
    // Fetch the list of doctors on component mount
    const loadDoctors = async () => {
      const userId = getValueFromToken("user_id");

      try {
        const response = await serviceFetchDoctorList(userId);
        if (response?.status) {
          const doctorsList = (response?.data?.doctors || []).map((doctor) => ({
            value: doctor.doctor_id,
            label: `${doctor.first_name} ${doctor.last_name}`,
          }));
          setDoctors(doctorsList);
        } else {
          console.error("Failed to fetch doctors list");
        }
      } catch (error) {
        console.error("Error fetching doctors list:", error);
      }
    };

    loadDoctors();
  }, []);

  const handleAppointmentSubmit = async (e) => {
    const clinicId = getClinicIdFromToken();
    e.preventDefault();
    try {
      const params = {
        patientId: patient.patient_id,
        doctorId: doctor.value,
        clinicId: clinicId,
      };
      const response = await serviceCreateAppointment(params);
      if (response?.status) {
        showToast(response?.message, "success");
        setTimeout(() => {
          navigate("/clinic/dashboard");
        }, 1000);
      } else {
        showToast(response?.message, "error");
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
    }
  };

  const handleDoctorChange = (selectedOption) => {
    setDoctor(selectedOption); // Update the selected doctor state
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        exit={{ opacity: 0, y: -50 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="flex items-center justify-between mb-4">
          <motion.h2
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-2xl font-semibold text-gray-800"
          >
            Patient Information
          </motion.h2>
          <motion.button
            onClick={handleEditClick}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="text-blue-500 focus:outline-none"
          >
            <FaPencilAlt size={20} />
          </motion.button>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 cursor-pointer"
          >
            {editedPatient.firstName && (
              <span className="text-2xl font-bold">
                {editedPatient.firstName.charAt(0)}
              </span>
            )}
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4" // Change to 3 columns on medium screens and above
        >
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">
              First Name
            </h3>
            {isEditMode ? (
              <input
                type="text"
                value={editedPatient.firstName}
                onChange={(e) =>
                  setEditedPatient({
                    ...editedPatient,
                    firstName: e.target.value,
                  })
                }
                className="border border-gray-300 rounded px-2 py-1 w-full" // Make the input full width
              />
            ) : (
              <p className="text-gray-800">{editedPatient.firstName}</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">
              Last Name
            </h3>
            {isEditMode ? (
              <input
                type="text"
                value={editedPatient.lastName}
                onChange={(e) =>
                  setEditedPatient({
                    ...editedPatient,
                    lastName: e.target.value,
                  })
                }
                className="border border-gray-300 rounded px-2 py-1 w-full" // Make the input full width
              />
            ) : (
              <p className="text-gray-800">{editedPatient.lastName}</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Age</h3>
            {isEditMode ? (
              <input
                type="number"
                value={editedPatient.age}
                onChange={(e) =>
                  setEditedPatient({ ...editedPatient, age: e.target.value })
                }
                className="border border-gray-300 rounded px-2 py-1 w-20" // Adjust the width of the age input
              />
            ) : (
              <p className="text-gray-800">{editedPatient.age}</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Gender</h3>
            {isEditMode ? (
              <Select
                options={genderOptions}
                value={genderOptions.find(
                  (option) => option.value === editedPatient.gender
                )}
                onChange={(selectedOption) =>
                  setEditedPatient({
                    ...editedPatient,
                    gender: selectedOption.value,
                  })
                }
                className="text-gray-700 text-sm w-40" // Add a fixed width
              />
            ) : (
              <p className="text-gray-800">{editedPatient.gender}</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Phone</h3>
            {isEditMode ? (
              <input
                type="text"
                value={editedPatient.primary_phone_number}
                onChange={(e) =>
                  setEditedPatient({
                    ...editedPatient,
                    primary_phone_number: e.target.value,
                  })
                }
                className="border border-gray-300 rounded px-2 py-1 w-full" // Make the input full width
              />
            ) : (
              <p className="text-gray-800">
                {editedPatient.primary_phone_number}
              </p>
            )}
          </div>
        </motion.div>
        {isEditMode && (
          <motion.button
            onClick={handleEditSubmit}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 flex justify-center"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <FaSpinner size={20} className="animate-spin" />
            ) : (
              "Save"
            )}
          </motion.button>
        )}
        {showAppointmentForm && (
          <form
            onSubmit={handleAppointmentSubmit}
            className="flex justify-between w-full items-end py-6 mt-2"
          >
            <div className="w-8/12 px-3">
              <div className="text-sm mb-1">Select Doctor:</div>
              <Select
                options={doctors}
                value={doctor}
                onChange={handleDoctorChange}
                placeholder="Select a doctor"
                className="text-gray-700 text-sm"
              />
            </div>
            <button
              type="submit"
              className="w-4/12 px-3 bg-blue-500 text-white rounded hover:bg-blue-700 px-4 py-1.5 focus:outline-none text-lg"
            >
              Book Appointment
            </button>
          </form>
        )}
      </motion.div>
    </>
  );
};

export default PatientDetails;
